import {
  Accordion,
  Button,
  Checkbox,
  CloseButton,
  Divider,
  Group,
  Radio,
  Stack,
  Text,
  TextInput,
  Tooltip,
} from "@mantine/core";
import { useState } from "react";
import { useProfileStore, useSessionStore } from "../../../../stores/UserStore";
import { useLanguage } from "../../../../stores/LanguageStore";
import { signup } from "../../../../api/AuthAPI";
import PhoneNumberInput from "../../../../components/Selectors/PhoneNumberInput";
import PasswordField from "../../../../components/View/PasswordField";
import CommunitySelector from "../../../../components/Sharing/CommunitySelector";
import BackButton from "../../../../components/View/BackButton";
import { IconUserPlus } from "@tabler/icons-react";
import { Link, useNavigate, useRouteLoaderData } from "react-router-dom";
import PageTitle from "../../../../components/View/PageTitle";
import { enableUserAccount } from "../../../../api/UserAPI";
import { appHost } from "../../../../functions/appHost";

const CreateUserPage = ({ type = "reseller" }) => {
  const lang = useLanguage((s) => s.language);
  const profile = useProfileStore((s) => s.profile);
  const reseller = useSessionStore((s) => s.reseller);
  const current = useRouteLoaderData(type);

  //#region Signup
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({
    email: false,
    password: false,
    phone: false,
    fName: false,
    lName: false,
    unknown: false,
  });
  const [pwdError, setPwdError] = useState(true);
  const [teams, setTeams] = useState(
    type === "team" ? [{ ...current, option_name: current.name }] : []
  );
  const [orgs, setOrgs] = useState(
    type === "organization" ? [{ ...current, option_name: current.name }] : []
  );

  const handlePhoneError = () => {
    setErrors({ phone: true });
    document.getElementById("phone").focus();
    setLoading(false);
  };
  const handlePwdError = () => {
    setErrors({ password: true });
    document.getElementById("pwd").focus();
    setLoading(false);
  };
  const handleEmailError = () => {
    setErrors({ email: true });
    document.getElementById("email").focus();
    setLoading(false);
  };
  const handleFNameError = () => {
    setErrors({ fName: true });
    document.getElementById("first_name").focus();
    setLoading(false);
  };
  const handleLNameError = () => {
    setErrors({ lName: true });
    document.getElementById("last_name").focus();
    setLoading(false);
  };
  const handleUnknownError = () => {
    setErrors({ unknown: true });
    setLoading(false);
  };

  const navigate = useNavigate();

  const handleSignup = (e) => {
    e.preventDefault();

    setErrors({});
    let formData = new FormData(e.target);
    const data = Object.fromEntries(formData);

    data.domain_saas =
      current?.domain_saas3 ||
      current?.domain_saas2 ||
      reseller?.domain_saas3 ||
      null;
    data.email_validation_url_success = `https://${appHost(reseller)}/email-validated`;
    data.email_validation_url_error = `https://${appHost(reseller)}/email-not-validated`;
    data.language = profile?.language;
    data.team_codes = teams?.length > 0 ? teams.map((x) => x.team_code) : null;
    data.organization_codes =
      orgs?.length > 0 ? orgs.map((x) => x.organization_code) : null;
    data.show_onboarding = false;
    data.send_email = data?.auto_activate === "on" ? false : true;

    if (pwdError) {
      return handlePwdError();
    } else {
      setLoading(true);
      signup(data)
        .then((response) => {
          if (data.auto_activate === "on" && response.data?.user_id) {
            enableUserAccount(response.data.user_id)
              .then((response) => {
                navigate("..");
              })
              .finally(() => setLoading(false));
          } else {
            navigate("..");
          }
        })
        .catch((error) => {
          setLoading(false);
          let data = error.response.data;
          if (data.state_message === "empty_last_name") {
            return handleLNameError();
          }
          if (data.state_message === "empty_first_name") {
            return handleFNameError();
          }
          if (data.state_message === "incorrect_email") {
            return handleEmailError();
          }
          if (data.state_message === "mail_error") {
            return handleEmailError();
          }
          if (data.state_message === "email_used") {
            return handleEmailError();
          }
          if (data.state_message === "incorrect_mobile_phone") {
            return handlePhoneError();
          }
          if (data.state_message === "mobile_phone_used") {
            return handlePhoneError();
          } else {
            handleUnknownError();
          }
        });
    }
  };
  //#endregion

  return (
    <Stack
      className="layout-block"
      flex={"1 1 auto"}
      component={"form"}
      onSubmit={handleSignup}
      w={"60%"}
      mx={"auto"}
      pr={24}
      pos={"relative"}
    >
      <CloseButton component={Link} to={".."} size={"lg"} />
      <PageTitle
        title={`${lang.users_list.create.dialog.title} ${current?.name} #${current?.id}`}
      />
      <Divider mt={8} />
      <Group wrap="nowrap" gap={"lg"}>
        <TextInput
          name="first_name"
          id="first_name"
          label={lang.signup.first_name}
          placeholder={lang.signup.first_name}
          required
          error={errors.fName}
          w={"100%"}
          autoFocus
          size="md"
        />
        <TextInput
          name="last_name"
          id="last_name"
          label={lang.signup.last_name}
          placeholder={lang.signup.last_name}
          required
          error={errors.lName}
          w={"100%"}
          size="md"
        />
      </Group>

      <Group wrap="nowrap" gap={"lg"}>
        <TextInput
          name="email"
          id="email"
          label={lang.signup.mail}
          placeholder={lang.signup.mail}
          required
          error={errors.email}
          w={"100%"}
          type="email"
          size="md"
        />

        <PhoneNumberInput
          defaultCountry={profile?.mobile_phone_country || "FR"}
          defaultValue={null}
          name={"mobile_phone"}
          label={lang.profile.mobile_phone}
          w={"100%"}
          id="phone"
          error={errors.phone}
          size="md"
        />
      </Group>

      <Group wrap="nowrap" gap={"lg"} align="start">
        <PasswordField
          error={pwdError}
          setError={setPwdError}
          id={"pwd"}
          size="md"
        />

        <Radio.Group
          name="sex"
          label={lang.signup.civility}
          w={"100%"}
          size="md"
        >
          <Group mt="xs">
            <Radio value="male" label={lang.signup.civility_male} size="md" />
            <Radio
              value="female"
              label={lang.signup.civility_female}
              size="md"
            />
          </Group>
        </Radio.Group>
      </Group>

      <Accordion variant="contained" mt={"xs"} defaultValue={"community"}>
        <Accordion.Item value={"community"}>
          <Accordion.Control bg={"var(--mantine-color-gray-0)"}>
            {lang.users_list.create.advanced}
          </Accordion.Control>
          <Accordion.Panel>
            <Stack>
              <CommunitySelector
                values={teams}
                onChange={setTeams}
                label={lang.teams.browser_tab_title}
                placeholder={lang.components.sharing.values.team_placeholder}
                type={"Team"}
                size="md"
              />
              <CommunitySelector
                values={orgs}
                onChange={setOrgs}
                label={lang.organizations.browser_tab_title}
                placeholder={lang.components.sharing.values.org_placeholder}
                type={"Organization"}
                size="md"
              />
            </Stack>
          </Accordion.Panel>
        </Accordion.Item>
      </Accordion>

      <Checkbox
        label={lang.users_list.create.auto_activate}
        name="auto_activate"
        size="md"
      />

      <Group justify="end" mt={"auto"} gap={"xl"}>
        <BackButton size="md" />

        <Tooltip
          label={lang.users_list.create.dialog.no_domain}
          disabled={type === "reseller" ? current?.domain_saas3 : true}
        >
          <Button
            type="submit"
            leftSection={<IconUserPlus size={18} />}
            loading={loading}
            size="md"
            disabled={type === "reseller" ? !current?.domain_saas3 : false}
          >
            {lang.users_list.create.dialog.submit_button}
          </Button>
        </Tooltip>
      </Group>
    </Stack>
  );
};

export default CreateUserPage;
